import React, { FC } from 'react';
import * as linkify from 'linkifyjs';
// @ts-ignore
import mention from 'linkifyjs/plugins/mention';
import Linkify from 'linkifyjs/react';
import reactStringReplace from 'react-string-replace';
import {
  getAllUsers,
  getProjectMembers,
  getProjectUsers
} from '../../models/Project';

import { Props } from './types';
import {
  getUserMentionTitle,
  getTaskMentionTitle,
  getUser,
  getTask,
  isTaskMention,
  parseMergedDescriptionSegments
} from './utils';
import { getBugherd } from 'appJS/models/Application';
import { UserMention } from './UserMention';
import { TaskMention } from './TaskMention';
import { MentionableTask } from '../../../clients/sidebar/views/CreateTask/create_task/types';

import styles from './index.module.css';

mention(linkify);

type ReadMoreTaskLinkProps = {
  taskId: number;
  mentionableTasks: MentionableTask[];
  setActiveTask?: (taskId: number) => void;
  bugherdUrl?: string;
  projectId?: number;
  className?: string;
};

const ReadMoreTaskLink: FC<ReadMoreTaskLinkProps> = ({
  taskId,
  mentionableTasks,
  setActiveTask,
  bugherdUrl,
  projectId,
  className
}) => {
  const task = mentionableTasks.find(
    t => t.localTaskId === taskId || t.id === taskId
  );
  const href =
    task?.viewURL ??
    (bugherdUrl && projectId
      ? `${bugherdUrl}/projects/${projectId}/tasks/${taskId}`
      : '#');

  return (
    <a
      href={href}
      rel="noreferrer"
      target="_blank"
      className={className}
      style={{ marginLeft: '4px' }}
      onClick={e => {
        e.stopPropagation();
        if (setActiveTask && task) {
          e.preventDefault();
          setActiveTask(task.id);
        }
        // else: allow default navigation to href (opens in new tab via target="_blank")
      }}
    >
      Read more
    </a>
  );
};

export const mentionRegex: RegExp = /@\[([^\[]+)\]\(([\d\)]+)\)/g;

export const highlightSearchText = (text: string, searchText?: string) =>
  searchText
    ? reactStringReplace(text, searchText, (match, index) => (
        <mark className={styles.searchMatch} key={index}>
          {match}
        </mark>
      ))
    : text;

const HighlightedMentions: FC<Props> = ({
  description = '',
  className,
  isComment,
  isPrivate,
  includeGuests,
  allUsers,
  projectUsers,
  projectMembers,
  mentionableTasks = [],
  setActiveTask,
  bugherdUrl,
  projectId,
  readMoreLinkClassName,
  icon = null,
  searchHighlightText
}) => {
  allUsers = allUsers || getAllUsers();
  projectUsers = includeGuests
    ? projectUsers || getProjectUsers()
    : projectMembers || getProjectMembers();

  const bugherd = getBugherd();
  if (bugherd) {
    const application = bugherd.application;
    if (application) {
      if (!mentionableTasks.length) {
        mentionableTasks = application.tasksCollection.mentionableTasks();
      }
      if (!setActiveTask) {
        setActiveTask = (taskId: number) => {
          const task = application.tasksCollection.get(taskId);
          bugherd.applicationView.openTask(task);
          bugherd.applicationRouter.navigate(
            `tasks/${task.get('local_task_id').toString()}`
          );
        };
      }
    }
  }

  const getTitle = (match: string, contextDescription: string): string => {
    const id = match.slice(1);
    if (isTaskMention(contextDescription, id)) {
      return getTaskMentionTitle({ id, task: getTask(id, mentionableTasks) });
    } else {
      const user = getUser(id, allUsers);
      const projectUser = getUser(id, projectUsers);
      return getUserMentionTitle({ id, projectUser, user });
    }
  };

  const linkifyOptions = (contextDescription: string) => ({
    tagName: {
      mention: 'span'
    },
    attributes: (match: string) => ({
      rel: 'noreferrer',
      title: getTitle(match, contextDescription)
    }),
    events: { click: (event: MouseEvent) => event.stopPropagation() },
    target: '_blank',
    format: {
      mention: (mention: string) => {
        const id = mention.slice(1);
        if (isTaskMention(contextDescription, id)) {
          const task = getTask(id, mentionableTasks);
          return (
            <TaskMention
              task={task}
              setActiveTask={setActiveTask}
              description={contextDescription}
              id={id}
            />
          );
        }
        const projectUser = getUser(id, projectUsers);
        const user = getUser(id, allUsers);
        return (
          <UserMention
            id={id}
            description={contextDescription}
            user={user}
            projectUser={projectUser}
            isComment={isComment}
            isPrivate={isPrivate}
          />
        );
      }
    }
  });

  const segments = parseMergedDescriptionSegments(description || '');
  const hasReadMoreSegments = segments.some(s => s.type === 'read_more');

  if (!hasReadMoreSegments) {
    return (
      // @ts-ignore Linkify accepts children at runtime; typings are incomplete
      <Linkify
        options={linkifyOptions(description || '') as any}
        className={className}
      >
        {highlightSearchText(
          (description || '').replace(mentionRegex, '@$2'),
          searchHighlightText
        )}
        {icon}
      </Linkify>
    );
  }

  return (
    <>
      {segments.map((segment, index) =>
        segment.type === 'text' ? (
          // @ts-ignore Linkify accepts children at runtime; typings are incomplete
          <Linkify
            key={index}
            options={linkifyOptions(segment.content) as any}
            className={className}
          >
            {highlightSearchText(
              segment.content.replace(mentionRegex, '@$2'),
              searchHighlightText
            )}
          </Linkify>
        ) : (
          <ReadMoreTaskLink
            key={index}
            taskId={segment.taskId}
            mentionableTasks={mentionableTasks}
            setActiveTask={setActiveTask}
            bugherdUrl={bugherdUrl}
            projectId={projectId}
            className={readMoreLinkClassName ?? className}
          />
        )
      )}
      {icon}
    </>
  );
};

export default HighlightedMentions;
